import React, {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
} from 'react';
import { useHistory, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import useRedirect from 'utils/hooks/useRedirect';
import {
  resetSubmissionDetail, toggleAddFollowersModal,
  toggleFileUploader,
} from 'store/entities/submissionDetail';
import {
  followSubmission, getReporters,
  getSubmission,
  unfollowSubmission,
} from 'store/entities/submissionDetail/thunks';

import { formatDateTime } from 'utils/dateUtils';
import { sanitizeHtml } from 'utils/stringUtils';

import FileStructure from './FileStructure';
import SubmissionHistory from './SubmissionHistory';
import ObligationMeta from './ObligationMeta';
import SubmissionMeta from './SubmissionMeta';

import UploadFile from './UploadFile';
import { Icon } from '@mdi/react';

import { mdiUpload } from '@mdi/js';
import Button, { ButtonGroup } from 'components/UIComponents/Button';
import { Column, Row, Section } from 'components/UIComponents/Layout';
import { Heading, Title } from 'components/UIComponents/Typography';
import StatusDecal from 'views/Submissions/List/StatusDecal';
import RichTextEditor from 'components/RichTextEditor';
import Messages, { Message } from 'components/UIComponents/Messages';
import LoadingSpinner from 'components/UIComponents/LoadingSpinner';
import AddFollowersFormModal from "./AddFollowersModal";

const SubmissionDetail = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const redirect = useRedirect();
  const { submissionId } = useParams();
  const dispatch = useDispatch();

  const {
    submissionDetail: {
      obligation = {},
      status,
      dueDate,
      messageDtos = [],
      submissionUploadDtos = [],
    },
    submissionDetailLoading,
    submissionFollowPending,
    submissionUnfollowPending,
    submissionDetailError,
    addFollowersModalOpen,
  } = useSelector(state => state.submissionDetail);

  const loadingState =
    submissionDetailLoading ||
    submissionFollowPending ||
    submissionUnfollowPending;
  const { tableDto, following } = obligation;

  const closeView = useCallback(
    () => (history.action === 'POP' ? redirect('') : history.goBack()),
    [history, redirect]
  );

  const handleFollowObligation = () =>
    following
      ? dispatch(unfollowSubmission(submissionId))
      : dispatch(followSubmission(submissionId));

  const { institutionDto} = obligation;
  const institutionId = institutionDto?.id;

  useEffect(() => {
    dispatch(getSubmission(submissionId));

    return () => dispatch(resetSubmissionDetail());
  }, [dispatch, submissionId]);

  useEffect(() => {
    if (submissionDetailError) closeView();
  }, [submissionDetailError, closeView]);

  useLayoutEffect(() => {
    const messageBox = document.getElementById('messages');
    if (messageDtos.length > 0 && messageBox) {
      messageBox.scrollTop = messageBox.scrollHeight;
    }
  }, [messageDtos]);

  useEffect(() => {
    const fetchReporters = async () => {
      if (!institutionId) {
        return;
      }
      const result = await dispatch(getReporters(institutionId));
    };

    fetchReporters();
  }, [addFollowersModalOpen, institutionId, dispatch]);

  return (
    <div>
      <UploadFile />
      <LoadingSpinner loading={loadingState} position="global" />
      <Section size="medium">
        <Title
          id="submission-detail-title"
          aria-label="Submission detail view title"
          prefix={
            dueDate && <StatusDecal date={new Date(dueDate)} status={status} />
          }
          title={<>{tableDto && tableDto.obligation}</>}
        >
          <ButtonGroup>
            {status === 'REJECTED' ||
            status === 'AWAITING_ARRIVAL' ||
            status === 'MISSED_DEADLINE' ? (
              <Button
                color="primary"
                outlined
                id="addDataFileButton"
                onClick={() => dispatch(toggleFileUploader(true))}
              >
                <Icon path={mdiUpload} size={1} />
                {t('datagate.external.submissions.button.addFile')}
              </Button>
            ) : (
              <span className="mr-4">
                {submissionUploadDtos[0]?.publicFileName}
              </span>
            )}

            <AddFollowersFormModal/>
            <Button
                id="obligation-add-followers-btn"
                aria-label="Obligation add followers button"
                onClick={() => dispatch(toggleAddFollowersModal())}
            >
              {t('datagate.external.submissionDetail.button.addFollowers')}
            </Button>

            <Button
              id="obligation-detail-follow-btn"
              aria-label="Obligation detail follow button"
              color={following ? 'success' : 'primary'}
              outlined={!following}
              onClick={handleFollowObligation}
            >
              {t(
                `datagate.external.submissionDetail.button.${
                  following ? 'following' : 'follow'
                }`
              )}
            </Button>
            <Button
              id="submission-detail-close-btn"
              aria-label="Submission detail close button"
              color="primary"
              onClick={closeView}
            >
              &#215; {t('datagate.external.submissionDetail.button.close')}
            </Button>
          </ButtonGroup>
        </Title>

        <ObligationMeta />

        <Row variableGap={{ desktop: 8 }} bordered hasBottomBorder>
          <Column size={6}>
            <SubmissionMeta />
          </Column>
          <Column size={6}>
            <>
              <Heading subtitle className="is-uppercase fw-bold">
                {t('datagate.external.submissions.subtitle.messages')}
              </Heading>
              <Messages>
                {messageDtos.map(message => (
                  <Message
                    header={`${message?.firstName} ${
                      message?.lastName
                    } ${formatDateTime(message.createdAt)}`}
                    body={
                      <p
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(message.content),
                        }}
                      />
                    }
                    isReply={message.keycloakId}
                  />
                ))}
              </Messages>
            </>
          </Column>
        </Row>
        <Row bordered>
          <Column size={6}>
            <SubmissionHistory />
          </Column>
          <Column size={6}>
            <Heading subtitle className="is-uppercase fw-bold">
              {t('datagate.external.submissionDetail.subtitle.newMessage')}
            </Heading>
            <Suspense fallback="">
              <RichTextEditor />
            </Suspense>
          </Column>
        </Row>
      </Section>
      <FileStructure />
    </div>
  );
};

export default SubmissionDetail;
